import axios from 'axios';
import type { GeneralSettings, GeneralSettingsUpdate, PublicBranding } from '../types/branding';
import type { CustomerLedgerEntry, CustomerPayment } from '../types/customerBilling';
import type { AppManagementSettings, DemoRequest, DemoRequestForm, DemoRequestStatus } from '../types/demoRequest';

// Cookie name for CSRF token (must match backend)
const CSRF_COOKIE_NAME = 'csrf_token';
const CSRF_HEADER_NAME = 'x-csrf-token';

/**
 * Helper to get CSRF token from cookie
 */
function getCsrfToken(): string | null {
  const match = document.cookie.match(new RegExp(`(^| )${CSRF_COOKIE_NAME}=([^;]+)`));
  return match ? match[2] : null;
}

export const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '/api',
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Send cookies with requests
});

// Add CSRF token + client platform to all requests.
// X-Client-Platform lets the backend tag activity/audit records by platform;
// a future iOS/Android app sends "iOS"/"Android" here instead of "Web".
api.interceptors.request.use((config) => {
  config.headers = config.headers ?? {};
  const csrfToken = getCsrfToken();
  if (csrfToken) {
    config.headers[CSRF_HEADER_NAME] = csrfToken;
  }
  config.headers['X-Client-Platform'] = 'Web';
  return config;
});

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // The session check is how the app asks whether anyone is signed in, so a 401 there is the normal
    // answer for a visitor rather than a failure. PrivateRoute and AdminRoute guard the protected pages.
    if (error.response?.status === 401 && error.config?.url !== '/auth/session') {
      // Redirect to login on authentication failure
      // Don't redirect if already on login/register page
      const publicPaths = ['/login', '/register', '/set-password', '/forgot-password', '/reset-password', '/terms', '/privacy', '/apply'];
      const isPublicPath = publicPaths.some((path) => window.location.pathname.startsWith(path));
      if (!isPublicPath) {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// API functions
export const unitsApi = {
  getAvailable: (params?: Record<string, string>) =>
    api.get('/units/available', { params }),
  getBrowsing: () => api.get<{ success: boolean; data: { enabled: boolean } }>('/units/browsing'),
};

export const bookingsApi = {
  getMine: () => api.get('/bookings/mine'),
  getById: (id: string) => api.get(`/bookings/${id}`),
  create: (data: { unitId: string; startDate: string }) =>
    api.post('/bookings', data),
  cancel: (id: string) => api.post(`/bookings/${id}/cancel`),
};

export const billingStatementsApi = {
  getMine: () => api.get('/billing-statements/mine'),
  /** The customer's account ledger, newest first; bookingId narrows it to one rental. */
  getMyLedger: (bookingId?: string) =>
    api.get<{ success: boolean; data: CustomerLedgerEntry[] }>('/billing-statements/mine/ledger', {
      params: bookingId ? { bookingId } : {},
    }),
};

export const paymentsApi = {
  createCheckoutSession: (bookingId: string) =>
    api.post('/payments/checkout-session', { bookingId }),
  getMethods: () => api.get('/payments/methods'),
  /** The customer's payment history, newest first. */
  getHistory: () =>
    api.get<{ success: boolean; data: CustomerPayment[] }>('/payments/history'),
};

export const adminApi = {
  getDashboard: () => api.get('/admin/dashboard'),
  getDailyDigest: () => api.get('/admin/dashboard/digest'),
  getInventoryStats: (params?: Record<string, string | undefined>) =>
    api.get('/admin/inventory-stats', { params }),
  getCustomers: (params?: Record<string, string | undefined>) =>
    api.get('/admin/customers', { params }),
  getAllUnits: (params?: Record<string, string | undefined>) =>
    api.get('/units', { params }),
  getAllBookings: (params?: Record<string, string | undefined>) =>
    api.get('/bookings', { params }),
  createBooking: (data: {
    customerId: string;
    accountId?: string;
    bookingType?: 'UNIT' | 'TIER';
    unitId?: string;
    unitIds?: string[];
    serviceTierId?: string;
    addOnIds?: string[];
    startDate: string;
    monthlyRate?: number;
    billingDayOfMonth?: number;
    notes?: string;
  }) => api.post('/bookings/admin', data),
  updateBooking: (id: string, data: {
    startDate?: string;
    endDate?: string | null;
    monthlyRate?: number;
    billingDayOfMonth?: number;
    gracePeriodDays?: number;
    lateFeeType?: string;
    lateFeeAmount?: number;
    invoiceDeliveryMethod?: string;
    autoGenerateInvoice?: boolean;
    paymentSource?: string;
    stripePaymentLinkId?: string | null;
    status?: string;
    notes?: string | null;
  }) => api.put(`/bookings/${id}`, data),
  deleteBooking: (id: string) => api.delete(`/bookings/${id}`),

  // Subscription Management
  setBookingSubscription: (bookingId: string, subscriptionId: string) =>
    api.put(`/bookings/${bookingId}/subscription`, { subscriptionId }),
  createSubscriptionCheckout: (bookingId: string) =>
    api.post(`/bookings/${bookingId}/subscription/checkout`),
  cancelBookingSubscription: (bookingId: string) =>
    api.delete(`/bookings/${bookingId}/subscription`),
  removeBookingFromAutopay: (bookingId: string) =>
    api.post(`/bookings/${bookingId}/remove-autopay`),

  exportReportXlsx: (type: string, params?: Record<string, string>) =>
    api.get(`/admin/reports/${type}/export`, { params, responseType: 'blob' }),

  // Payments & Accounting
  getPayments: (params?: Record<string, string | undefined>) =>
    api.get('/admin/payments', { params }),
  getPaymentStats: () => api.get('/admin/payments/stats'),
  recordManualPayment: (data: {
    // Exactly one target: an invoice or a billing statement.
    invoiceId?: string;
    billingStatementId?: string;
    amount: string;
    paymentMethod: string;
    /** YYYY-MM-DD; defaults to today server-side. */
    paidAt?: string;
    referenceNumber?: string;
    notes?: string;
  }) => api.post('/admin/payments/manual', data),

  // Payment Methods
  getPaymentMethods: () => api.get('/admin/payment-methods'),
  createPaymentMethod: (data: { name: string; description?: string }) =>
    api.post('/admin/payment-methods', data),
  updatePaymentMethod: (id: string, data: { name?: string; description?: string; isActive?: boolean }) =>
    api.put(`/admin/payment-methods/${id}`, data),
  deletePaymentMethod: (id: string) => api.delete(`/admin/payment-methods/${id}`),

  // Service Tiers
  getServiceTiers: (tenantId?: string) => api.get('/admin/service-tiers', { params: tenantId ? { tenantId } : undefined }),
  createServiceTier: (data: { name: string; description?: string; monthlyPrice: number; sortOrder?: number }) =>
    api.post('/admin/service-tiers', data),
  updateServiceTier: (id: string, data: { name?: string; description?: string; monthlyPrice?: number; sortOrder?: number; isActive?: boolean }) =>
    api.put(`/admin/service-tiers/${id}`, data),
  deleteServiceTier: (id: string) => api.delete(`/admin/service-tiers/${id}`),

  // Add-On Services
  getAddOnServices: (tenantId?: string) => api.get('/admin/addon-services', { params: tenantId ? { tenantId } : undefined }),
  createAddOnService: (data: { name: string; description?: string; price: number; pricingType?: string; pricingUnit?: string | null; sortOrder?: number }) =>
    api.post('/admin/addon-services', data),
  updateAddOnService: (id: string, data: { name?: string; description?: string; price?: number; pricingType?: string; pricingUnit?: string | null; sortOrder?: number; isActive?: boolean }) =>
    api.put(`/admin/addon-services/${id}`, data),
  deleteAddOnService: (id: string) => api.delete(`/admin/addon-services/${id}`),

  // Billing Statements (Account-based billing)
  getBillingStatements: (params?: Record<string, string | undefined>) =>
    api.get('/billing-statements', { params }),
  getBillingStatementById: (id: string) =>
    api.get(`/billing-statements/${id}`),
  generateBillingStatement: (data: {
    accountId: string;
    periodStart?: string;
    periodEnd?: string;
    dueDate?: string;
    gracePeriodDays?: number;
    taxRate?: number;
    sendImmediately?: boolean;
    notes?: string;
  }) => api.post('/billing-statements/generate', data),
  generateMonthlyStatements: (data: {
    taxRate?: number;
    sendImmediately?: boolean;
    accountScope?: 'ALL' | 'SELECTED';
  }) => api.post('/billing-statements/generate-monthly', data),
  getBillingStatementPayments: (id: string) =>
    api.get(`/billing-statements/${id}/payments`),
  updateBillingStatementStatus: (id: string, status: string, paidAt?: string) =>
    api.patch(`/billing-statements/${id}/status`, { status, paidAt }),
  updateBillingStatement: (id: string, data: {
    statementDate?: string;
    dueDate?: string;
    periodStart?: string | null;
    periodEnd?: string | null;
    notes?: string | null;
    lateFee?: number;
  }) => api.put(`/billing-statements/${id}`, data),
  duplicateBillingStatement: (id: string, data?: {
    statementDate?: string;
    dueDate?: string;
    periodStart?: string | null;
    periodEnd?: string | null;
    notes?: string | null;
  }) => api.post(`/billing-statements/${id}/duplicate`, data || {}),
  applyLateFees: (id: string) =>
    api.post(`/billing-statements/${id}/apply-late-fees`),
  sendBillingStatement: (id: string, deliveryMethod: string) =>
    api.post(`/billing-statements/${id}/send`, { deliveryMethod }),
  deleteBillingStatement: (id: string) =>
    api.delete(`/billing-statements/${id}`),

  // Reminders
  getReminders: (params?: Record<string, string | undefined>) =>
    api.get('/admin/reminders', { params }),
  createReminder: (data: {
    customerId: string;
    bookingId?: string;
    reminderType: string;
    daysBefore: string;
    channel: string;
  }) => api.post('/admin/reminders', data),
  updateReminder: (id: string, data: {
    reminderType?: string;
    daysBefore?: string;
    channel?: string;
    isActive?: boolean;
  }) => api.put(`/admin/reminders/${id}`, data),
  deleteReminder: (id: string) => api.delete(`/admin/reminders/${id}`),

  // Tenant
  getTenants: (params?: Record<string, string | undefined>) =>
    api.get('/admin/tenants', { params }),
  createTenant: (data: {
    name: string;
    subdomain?: string;
    customDomain?: string;
    pricingPlan?: string;
    status?: string;
    settings?: string;
  }) => api.post('/admin/tenants', data),
  updateTenant: (id: string, data: {
    name?: string;
    subdomain?: string;
    customDomain?: string;
    pricingPlan?: string;
    status?: string;
    settings?: string;
    invoiceFromEmail?: string;
  }) => api.put(`/admin/tenants/${id}`, data),
  deleteTenant: (id: string) => api.delete(`/admin/tenants/${id}`),
  getEmailSenders: () => api.get('/admin/email-senders'),
  createEmailSender: (data: { kind: 'DOMAIN' | 'ADDRESS'; value: string }) => api.post('/admin/email-senders', data),
  deleteEmailSender: (id: string) => api.delete(`/admin/email-senders/${id}`),
  syncEmailSenders: () => api.post('/admin/email-senders/sync'),

  // Customer CRUD
  createCustomer: (data: {
    tenantId?: string; // Optional: SUPERADMIN can specify tenant
    customerType: 'PERSONAL' | 'BUSINESS';
    businessName?: string; // Required for BUSINESS
    email: string;
    firstName?: string; // Required for PERSONAL, optional for BUSINESS
    lastName?: string; // Required for PERSONAL, optional for BUSINESS
    phone?: string;
    billingAddress?: string;
    city?: string;
    state?: string;
    zipCode?: string;
  }) => api.post('/admin/customers', data),
  updateCustomer: (id: string, data: {
    customerType?: 'PERSONAL' | 'BUSINESS';
    businessName?: string;
    firstName?: string;
    lastName?: string;
    phone?: string;
    billingAddress?: string;
    city?: string;
    state?: string;
    zipCode?: string;
    status?: string;
  }) => api.put(`/admin/customers/${id}`, data),
  // Soft delete: archives the customer and revokes their login
  archiveCustomer: (id: string) => api.delete(`/admin/customers/${id}`),
  restoreCustomer: (id: string) => api.post(`/admin/customers/${id}/restore`),

  // Customer Login Management
  sendCustomerPasswordSetup: (customerId: string) =>
    api.post(`/admin/customers/${customerId}/send-password-setup`),
  createCustomerUserAccount: (customerId: string) =>
    api.post(`/admin/customers/${customerId}/create-user-account`),
  suspendCustomerLogin: (customerId: string) =>
    api.post(`/admin/customers/${customerId}/suspend-login`),
  reactivateCustomerLogin: (customerId: string) =>
    api.post(`/admin/customers/${customerId}/reactivate-login`),

  // Admin Users CRUD (SUPERADMIN only)
  getAdminUsers: (params?: Record<string, string | undefined>) =>
    api.get('/admin/users', { params }),
  getTenantUsers: (params?: Record<string, string | undefined>) =>
    api.get('/admin/tenant-users', { params }),
  createAdminUser: (data: {
    email: string;
    firstName: string;
    lastName: string;
    phone?: string;
    role: 'SUPERADMIN' | 'TENANT_ADMIN';
    tenantId?: string | null; // Required for TENANT_ADMIN, null for SUPERADMIN
  }) => api.post('/admin/users', data),
  updateAdminUser: (id: string, data: {
    firstName?: string;
    lastName?: string;
    phone?: string;
    status?: string;
    smsOverdueNotifications?: boolean;
  }) => api.put(`/admin/users/${id}`, data),
  deleteAdminUser: (id: string) => api.delete(`/admin/users/${id}`),

  // Activity & reporting (login activity, online users, audit log, counts)
  getLoginActivity: (params?: Record<string, string | undefined>) =>
    api.get('/admin/activity/logins', { params }),
  getOnlineUsers: (params?: Record<string, string | undefined>) =>
    api.get('/admin/activity/online', { params }),
  getAuditLog: (params?: Record<string, string | undefined>) =>
    api.get('/admin/activity/audit', { params }),
  getActivityStats: (params?: Record<string, string | undefined>) =>
    api.get('/admin/activity/stats', { params }),

  getNotificationLogs: (params?: Record<string, string | undefined>) =>
    api.get('/admin/notification-logs', { params }),
  getSystemStatusJobs: () => api.get('/admin/system-status/jobs'),
  getSystemStatusNotifications: (params?: Record<string, string | undefined>) =>
    api.get('/admin/system-status/notifications', { params }),
  getSystemStatusApiErrors: (params?: Record<string, string | undefined>) =>
    api.get('/admin/system-status/api-errors', { params }),
  getSystemStatusStorage: () => api.get('/admin/system-status/storage'),
  getSystemStatusDatabase: () => api.get('/admin/system-status/database'),
  getSystemStatusServiceHealth: () => api.get('/admin/system-status/service-health'),
  getSystemStatusScheduledJobs: () => api.get('/admin/system-status/scheduled-jobs'),
  getJobExecutionLogs: (params?: Record<string, string | undefined>) =>
    api.get('/admin/system-status/job-execution-logs', { params }),

  // Locations
  getLocations: () => api.get('/admin/locations'),

  // Units CRUD
  createUnit: (data: {
    unitNumber: string;
    unitType: 'OUTDOOR' | 'INDOOR';
    locationId: string;
    length: number;
    width: number;
    monthlyRate: number;
    description?: string;
    features?: string;
    status?: string;
  }) => api.post('/units', data),
  updateUnit: (id: string, data: {
    unitNumber?: string;
    unitType?: 'OUTDOOR' | 'INDOOR';
    locationId?: string;
    length?: number;
    width?: number;
    monthlyRate?: number;
    description?: string;
    features?: string;
    status?: string;
  }) => api.put(`/units/${id}`, data),

  // Accounts (multi-account billing)
  getAccounts: (params?: Record<string, string | undefined>) =>
    api.get('/admin/accounts', { params }),
  createAccount: (data: {
    customerId: string;
    accountName: string;
    accountType?: 'PERSONAL' | 'BUSINESS';
    billingName?: string;
    billingAddress?: string;
    city?: string;
    state?: string;
    zipCode?: string;
    billingNotes?: string;
    serviceTierId?: string | null;
    unitDiscountPercent?: number;
    autoBillingEnabled?: boolean;
  }) => api.post('/admin/accounts', data),
  updateAccount: (id: string, data: {
    accountName?: string;
    accountType?: 'PERSONAL' | 'BUSINESS';
    billingName?: string | null;
    billingAddress?: string | null;
    city?: string | null;
    state?: string | null;
    zipCode?: string | null;
    billingNotes?: string | null;
    serviceTierId?: string | null;
    unitDiscountPercent?: number;
    autoBillingEnabled?: boolean;
    status?: 'ACTIVE' | 'INACTIVE' | 'SUSPENDED';
  }) => api.put(`/admin/accounts/${id}`, data),
  deleteAccount: (id: string) => api.delete(`/admin/accounts/${id}`),

  // Settings -> General (company branding, contact details, hours, colors, time zone)
  getDemoRequests: (params?: Record<string, string | undefined>) =>
    api.get<{ success: boolean; data: { items: DemoRequest[]; total: number } }>('/admin/app-management/demo-requests', { params }),
  updateDemoRequestStatus: (id: string, status: DemoRequestStatus) =>
    api.put(`/admin/app-management/demo-requests/${id}/status`, { status }),
  getAppManagementSettings: () =>
    api.get<{ success: boolean; data: AppManagementSettings }>('/admin/app-management/settings'),
  updateAppManagementSettings: (data: { notifyEmail: string }) =>
    api.put<{ success: boolean; data: AppManagementSettings }>('/admin/app-management/settings', data),
  getGeneralSettings: () => api.get<{ success: boolean; data: GeneralSettings }>('/admin/general-settings'),
  updateGeneralSettings: (data: GeneralSettingsUpdate) =>
    api.put<{ success: boolean; data: GeneralSettings }>('/admin/general-settings', data),
  uploadLogo: (data: { logo: string; contentType: string }) =>
    api.post<{ success: boolean; data: GeneralSettings }>('/admin/general-settings/logo', data),
  deleteLogo: () => api.delete<{ success: boolean; data: GeneralSettings }>('/admin/general-settings/logo'),

  // Scheduling Settings
  getSchedulingSettings: () => api.get('/scheduling-settings'),
  getSchedulingBillingAccounts: () => api.get('/scheduling-settings/billing-accounts'),
  updateSchedulingSettings: (data: Record<string, unknown>) =>
    api.patch('/scheduling-settings', data),

  // Entra ID / SSO settings
  getEntraIdConfig: () => api.get('/admin/system-status/entra-id'),
  updateEntraIdConfig: (data: { enabled: boolean; clientId?: string; tenantId?: string; redirectUri?: string }) =>
    api.put('/admin/system-status/entra-id', data),
};

// Document Management API
export const documentsApi = {
  getMine: () => api.get('/documents/mine'),
  getAll: (params?: Record<string, string | undefined>) =>
    api.get('/documents', { params }),
  getDownloadUrl: (id: string) => api.get(`/documents/${id}/download`),
  upload: (data: {
    name: string;
    fileData: string;
    contentType: string;
    originalName: string;
    category?: string;
    tags?: string[];
    description?: string;
    bookingId?: string;
    customerId?: string;
  }) => api.post('/documents', data),
  delete: (id: string) => api.delete(`/documents/${id}`),
};

// Commercial lease applications (admin)
type LeaseSignaturePayload = { typedName: string; image: string; consent: true };

export const leaseApplicationsApi = {
  list: (params?: Record<string, string | undefined>) => api.get('/lease-applications', { params }),
  get: (id: string, reveal = false) =>
    api.get(`/lease-applications/${id}`, { params: reveal ? { reveal: 'true' } : undefined }),
  create: (data: Record<string, unknown>) => api.post('/lease-applications', data),
  update: (id: string, data: Record<string, unknown>) => api.put(`/lease-applications/${id}`, data),
  resend: (id: string, signerId: string) => api.post(`/lease-applications/${id}/resend`, { signerId }),
  decide: (id: string, data: { decision: 'APPROVE' | 'DENY'; notes?: string | null; notifyApplicant: boolean }) =>
    api.post(`/lease-applications/${id}/decision`, data),
  sendLease: (
    id: string,
    data: { form: 'GENERIC' | 'GENERIC_MULTI_TENANT' | 'GENERIC_RESIDENTIAL' | 'GAR_CF31' | 'GAR_CF34' | 'GAR_F913'; terms: Record<string, unknown>; includeLetterhead?: boolean }
  ) =>
    api.post(`/lease-applications/${id}/send-lease`, data),
  previewLease: (
    id: string,
    data: { form: 'GENERIC' | 'GENERIC_MULTI_TENANT' | 'GENERIC_RESIDENTIAL' | 'GAR_CF31' | 'GAR_CF34' | 'GAR_F913'; terms: Record<string, unknown>; includeLetterhead?: boolean }
  ) =>
    api.post(`/lease-applications/${id}/preview-lease`, data, { responseType: 'blob' }),
  printApplication: (id: string, full: boolean) =>
    api.get(`/lease-applications/${id}/application.pdf`, { params: full ? { full: 'true' } : undefined, responseType: 'blob' }),
  saveLeaseDraft: (
    id: string,
    data: { form: 'GENERIC' | 'GENERIC_MULTI_TENANT' | 'GENERIC_RESIDENTIAL' | 'GAR_CF31' | 'GAR_CF34' | 'GAR_F913'; terms: Record<string, unknown>; includeLetterhead?: boolean }
  ) => api.put(`/lease-applications/${id}/lease-draft`, data),
  getGarTemplate: (formId: 'cf31' | 'cf34' | 'f913') => api.get(`/lease-applications/templates/${formId}`),
  uploadGarTemplate: (formId: 'cf31' | 'cf34' | 'f913', data: { fileName: string; data: string; licenseAttested: true }) =>
    api.post(`/lease-applications/templates/${formId}`, data),
  createDirectLease: (data: {
    applicationType: 'COMMERCIAL' | 'RESIDENTIAL';
    tenantName: string;
    tenantEmail: string;
    tenantPhone?: string | null;
    propertyId?: string | null;
    propertyAddress?: string | null;
    propertySuite?: string | null;
    monthlyRent: number;
    securityDeposit: number;
  }) => api.post('/lease-applications/direct-lease', data),
  countersign: (id: string, data: LeaseSignaturePayload & { title: string; sendPortalInvite: boolean }) =>
    api.post(`/lease-applications/${id}/countersign`, data),
  sendPortalInvite: (id: string) => api.post(`/lease-applications/${id}/portal-invite`),
  recordFeePayment: (id: string, data: { amount: number; method: string; reference?: string | null }) =>
    api.post(`/lease-applications/${id}/record-fee`, data),
  cancel: (id: string) => api.post(`/lease-applications/${id}/cancel`),
  reopen: (id: string, data: { emailApplicant: boolean }) => api.post(`/lease-applications/${id}/reopen`, data),
  changeType: (id: string, applicationType: 'COMMERCIAL' | 'RESIDENTIAL') =>
    api.patch(`/lease-applications/${id}/type`, { applicationType }),
  changeProperty: (id: string, data: { propertyId: string | null; refreshLeaseTerms?: boolean }) =>
    api.patch(`/lease-applications/${id}/property`, data),
  remove: (id: string) => api.delete(`/lease-applications/${id}`),
  uploadDocument: (id: string, data: { fileName: string; contentType: string; data: string; label?: string | null }) =>
    api.post(`/lease-applications/${id}/documents`, data),
  uploadExecutedLease: (
    id: string,
    data: { fileName: string; contentType: string; data: string; signedOn: string; notes?: string | null; sendPortalInvite: boolean }
  ) => api.post(`/lease-applications/${id}/executed-lease`, data),
  getAttachmentUrl: (id: string, attachmentId: string) =>
    api.get(`/lease-applications/${id}/attachments/${attachmentId}`),
};

// Properties an application or lease can be built from, so an address is typed once
export interface LeasePropertyPayload {
  name: string;
  propertyType: 'COMMERCIAL' | 'RESIDENTIAL' | 'BOTH';
  streetAddress: string;
  suite?: string | null;
  city: string;
  state: string;
  zip: string;
  county?: string | null;
  taxParcelId?: string | null;
  ownerOfRecord?: string | null;
  sqFt?: number | null;
  description?: string | null;
  notes?: string | null;
  isActive?: boolean;
}

export const leasePropertiesApi = {
  list: (includeArchived = false) =>
    api.get('/lease-properties', { params: includeArchived ? { includeArchived: 'true' } : undefined }),
  create: (data: LeasePropertyPayload) => api.post('/lease-properties', data),
  update: (id: string, data: Partial<LeasePropertyPayload>) => api.put(`/lease-properties/${id}`, data),
  remove: (id: string) => api.delete(`/lease-properties/${id}`),
};

// Company branding for the customer portal (company comes from the site's domain; no login)
export const publicBrandingApi = {
  get: () => api.get<{ success: boolean; data: PublicBranding }>('/public/branding'),
};

// The sales page's demo form (no login)
export const publicDemoRequestApi = {
  submit: (data: DemoRequestForm & { fillMs: number }) => api.post('/public/demo-requests', data),
};

// Commercial lease applications (applicant / guarantor private link; no login)
export const publicLeaseApi = {
  get: (token: string) => api.get(`/public/lease-applications/${token}`),
  saveDraft: (token: string, formData: unknown) =>
    api.put(`/public/lease-applications/${token}/draft`, { formData }),
  upload: (token: string, data: { fileName: string; contentType: string; data: string; label?: string | null }) =>
    api.post(`/public/lease-applications/${token}/attachments`, data),
  deleteAttachment: (token: string, attachmentId: string) =>
    api.delete(`/public/lease-applications/${token}/attachments/${attachmentId}`),
  submit: (token: string, data: { formData: unknown; signature: LeaseSignaturePayload }) =>
    api.post(`/public/lease-applications/${token}/submit`, data),
  signLease: (token: string, data: { signature: LeaseSignaturePayload; leaseHash?: string | null; guarantyHash?: string | null }) =>
    api.post(`/public/lease-applications/${token}/sign`, data),
  payApplicationFee: (token: string) => api.post(`/public/lease-applications/${token}/pay-fee`),
  getExecutedLease: (token: string) => api.get(`/public/lease-applications/${token}/executed-lease`),
  leasePdfUrl: (token: string) => `${api.defaults.baseURL ?? '/api'}/public/lease-applications/${token}/lease.pdf`,
};
